'use client'

import Link from 'next/link'
import React from 'react'
import { useSiteContext, useTranslations } from '../hooks'
import { stripTrailingSlash } from '../utils/urls'

export type PathFragment = {
  url: string
  clientNavigationRoute: string
  displayName: string
}

type BreadcrumbSchemaItem = {
  '@type': string
  position: number
  name: string
  item?: string
}

type BreadcrumbSchema = {
  '@context': string
  '@type': string
  itemListElement: BreadcrumbSchemaItem[]
}

const breadcrumbSchema = (
  path: PathFragment[],
  SiteBaseUrl: string,
  translate: (arg0: string) => string
): BreadcrumbSchema => {
  const items = path.map((item: PathFragment, i: number) => {
    const listItem: BreadcrumbSchemaItem = {
      '@type': 'ListItem',
      position: i + 1,
      name: i === 0 ? translate('Homepage') : item.displayName,
    }

    if (i === 0) {
      listItem.item = SiteBaseUrl
    } else if (i !== path.length - 1) {
      listItem.item = `${SiteBaseUrl}${item.url}`
    }

    return listItem
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  }
}

type Props = {
  path: PathFragment[]
  shallow?: boolean
}

export const Breadcrumbs = ({ path, shallow = false }: Props) => {
  const { translate } = useTranslations()
  const { isProxied, siteUrl } = useSiteContext()
  const normalizedPath = (path: PathFragment[], isProxied: boolean) => {
    const filtredBreadcrumbs = path.filter(Boolean)
    let resultBreadcrumbs
    // if one  - add home page link, if multiple than replace first link with home page
    if (filtredBreadcrumbs.length === 1) {
      resultBreadcrumbs = [
        { displayName: translate('Homepage'), clientNavigationRoute: '/', url: '/' },
        filtredBreadcrumbs[0],
      ]
    } else {
      resultBreadcrumbs = [
        { displayName: translate('Homepage'), clientNavigationRoute: '/', url: '/' },
        ...filtredBreadcrumbs.slice(1),
      ]
    }

    return resultBreadcrumbs.map(breadcrumb => {
      return {
        ...breadcrumb,
        url: isProxied ? `${siteUrl}${breadcrumb.url}` : breadcrumb.url,
      }
    })
  }

  const breadcrumbs = normalizedPath(path, isProxied)

  return (
    <nav className="c-breadcrumbs d-print-none">
      {path && path.length ? (
        <ol className="c-breadcrumbs__list">
          {breadcrumbs.map((breadcrumb, i: number) => {
            const isLast = i === breadcrumbs.length - 1
            const order = breadcrumbs.length - i - 1
            const url = breadcrumb.url === '/' ? breadcrumb.url : stripTrailingSlash(breadcrumb.url)

            return (
              <li key={i} style={{ order }}>
                {isLast ? (
                  <span>{breadcrumb.displayName}</span> // текущая страница
                ) : (
                  <Link
                    prefetch={false}
                    as={url}
                    shallow={shallow}
                    href={breadcrumb.clientNavigationRoute ?? ''}>
                    {breadcrumb.displayName}
                  </Link>
                )}
              </li>
            )
          })}
        </ol>
      ) : null}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema(path, siteUrl, translate)),
        }}
      />
    </nav>
  )
}

export default Breadcrumbs
